import React from "react";
import { entityForToken, normalizeRichMessage, safeHref } from "./messageFormat.mjs";

function renderInline(value) {
  const source = normalizeRichMessage(value);
  const parts = source.split(/(\*\*[^*]+\*\*|\*[^*]+\*|\[[^\]]+\]\(https:\/\/[^\s)]+\)|https:\/\/[^\s<>]+|[^\s<>]+@[^\s<>]+|\+?[0-9][0-9()\s.-]{6,}[0-9])/g);
  return parts.filter(Boolean).map((part, index) => {
    const strong = part.match(/^\*\*([^*]+)\*\*$/);
    if (strong) return <strong key={index} className="font-semibold">{strong[1]}</strong>;
    const italic = part.match(/^\*([^*]+)\*$/);
    if (italic) return <em key={index}>{italic[1]}</em>;
    const markdownLink = part.match(/^\[([^\]]+)\]\((https:\/\/[^\s)]+)\)$/);
    if (markdownLink && safeHref(markdownLink[2])) {
      return <a key={index} href={markdownLink[2]} target="_blank" rel="noopener noreferrer" className="underline underline-offset-2">{markdownLink[1]}</a>;
    }
    const entity = entityForToken(part);
    if (entity) {
      const external = entity.type === "url";
      return <a key={index} href={entity.href} {...(external ? { target: "_blank", rel: "noopener noreferrer" } : {})} className="underline underline-offset-2">{entity.value}</a>;
    }
    return <React.Fragment key={index}>{part}</React.Fragment>;
  });
}

function blocks(value) {
  const result = [];
  let paragraph = [];
  let list = null;
  const flushParagraph = () => {
    if (paragraph.length) result.push({ type: "paragraph", text: paragraph.join(" ") });
    paragraph = [];
  };
  const flushList = () => {
    if (list?.items.length) result.push(list);
    list = null;
  };
  for (const rawLine of normalizeRichMessage(value).split("\n")) {
    const line = rawLine.trim();
    const bullet = line.match(/^[-*]\s+(.+)$/);
    const numbered = line.match(/^\d+[.)]\s+(.+)$/);
    if (bullet || numbered) {
      flushParagraph();
      const type = numbered ? "ordered" : "unordered";
      if (!list || list.type !== type) { flushList(); list = { type, items: [] }; }
      list.items.push((bullet || numbered)[1]);
      continue;
    }
    flushList();
    if (!line) flushParagraph(); else paragraph.push(line);
  }
  flushParagraph(); flushList();
  return result;
}

/** Safely renders the restricted AfriMed message format; never uses HTML injection. */
export default function SafeMessageContent({ value, className = "" }) {
  const content = React.useMemo(() => blocks(value), [value]);
  return <div className={["break-words", className].filter(Boolean).join(" ")}>{content.map((block, index) => {
    if (block.type === "ordered") return <ol key={index} className="ml-5 list-decimal space-y-1">{block.items.map((item, itemIndex) => <li key={itemIndex}>{renderInline(item)}</li>)}</ol>;
    if (block.type === "unordered") return <ul key={index} className="ml-5 list-disc space-y-1">{block.items.map((item, itemIndex) => <li key={itemIndex}>{renderInline(item)}</li>)}</ul>;
    return <p key={index} className="whitespace-pre-wrap">{renderInline(block.text)}</p>;
  })}</div>;
}
